import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useCommon } from '../../context/CommonContext';
import { useResponsive } from '../../context/ResponsiveContext';
import { RoutesPathE } from '../../declarations/enums/routes';
import useReactRouter from '../../hooks/useReactRouter';
import Button from '../Button/Button';
import { LeftChevronIcon } from '../Icon/Icon';
import {
  BreadcrumbsWrapperStyled,
  LinkStyled,
  MobileCrumbStyled,
  NumberStyled,
} from './Breadcrumbs.styled';

type BreadcrumbsPropsT = {
  isNextPageAvailable?: boolean;
  onClickBreadcrumb?: () => void;
};

const SUSTAINABLE_ACTIONS_ONLY_BREADCRUMBS = [
  {
    path: RoutesPathE.SUSTAINABLE_ACTIONS_PAGE,
    label: <FormattedMessage id="breadcrumbs.select" defaultMessage="Select" />,
  },
  {
    path: RoutesPathE.PAYMENT_DETAILS_PAGE,
    label: <FormattedMessage id="breadcrumbs.paymentDetails" defaultMessage="Payment Details" />,
  },
  {
    path: RoutesPathE.CONFIRMATION_PAGE,
    label: <FormattedMessage id="breadcrumbs.confirmation" defaultMessage="Confirmation" />,
  },
];

const Breadcrumbs: React.FC<BreadcrumbsPropsT> = ({
  isNextPageAvailable = true,
  onClickBreadcrumb,
}) => {
  const { isTransportDashboardType } = useCommon();
  const { isMobile } = useResponsive();
  const { navigateTo, getUrlQueryParams } = useReactRouter();
  const queryParams = getUrlQueryParams<{ calculate: string }>();

  const breadcrumbs = useMemo(
    () =>
      queryParams?.calculate
        ? [
            {
              path: RoutesPathE.HOME_PAGE,
              label: isTransportDashboardType ? (
                <FormattedMessage id="breadcrumbs.home" defaultMessage="Home" />
              ) : (
                <FormattedMessage id="breadcrumbs.yourStay" defaultMessage="Your Stay" />
              ),
            },
            {
              path: RoutesPathE.TRANSPORTATION_PAGE,
              label: (
                <FormattedMessage
                  id="breadcrumbs.yourTransportation"
                  defaultMessage="yourTransportation"
                />
              ),
            },
            {
              path: RoutesPathE.CARBON_OFFSETS_PAGE,
              //TODO Figure out why was implemented that way
              match: [RoutesPathE.SUSTAINABLE_ACTIONS_PAGE, RoutesPathE.PAYMENT_DETAILS_PAGE],
              label: (
                <FormattedMessage
                  id="breadcrumbs.offsetYourCarbonFootprint"
                  defaultMessage="Offset your carbon footprint"
                />
              ),
            },
          ]
        : SUSTAINABLE_ACTIONS_ONLY_BREADCRUMBS,
    [isTransportDashboardType, queryParams?.calculate]
  );

  const location = useLocation();
  const currentCrumb = breadcrumbs.find(
    ({ path, match }) =>
      location.pathname === path || (!!match && match.includes(location.pathname as RoutesPathE))
  );
  const currentPageIndex = breadcrumbs.indexOf(currentCrumb as typeof breadcrumbs[0]);

  const handleOnClick = (index: number) => {
    // handle step back
    if (index < 0) {
      return;
    }

    // handle click of 1st breadcrumb element for SA page if user chose only SA on the home page
    // queryParams?.calculate doesn't exist in this case
    if (index === 0 && !queryParams?.calculate) {
      navigateTo(breadcrumbs[0].path, {}, true);

      return;
    }

    if (index === 0) {
      navigateTo(breadcrumbs[0].path);

      return;
    }

    if (currentPageIndex > index) {
      navigateTo(breadcrumbs[index].path, {}, true);

      return;
    }
    // handle step back

    // handle go to next page
    const isDisabledNextStep = index >= currentPageIndex && !isNextPageAvailable;

    if (isDisabledNextStep) {
      return;
    }

    if (index > currentPageIndex && !!onClickBreadcrumb) {
      onClickBreadcrumb();

      return;
    }

    navigateTo(breadcrumbs[index].path, {}, true);
    // handle go to next page
  };

  return (
    <BreadcrumbsWrapperStyled>
      {breadcrumbs.map(({ path, label, match }, index) => {
        const isMatchPathname = !!match && match.includes(location.pathname as RoutesPathE);
        const isActive = location.pathname === path;

        if (isMobile && isActive) {
          return (
            <MobileCrumbStyled key={index}>
              <Button modifier="inline" onClick={() => handleOnClick(index - 1)}>
                <LeftChevronIcon />
              </Button>

              <LinkStyled isActive modifier="inline">
                <NumberStyled>{index + 1}</NumberStyled>
                {label}
              </LinkStyled>

              <Button modifier="inline" onClick={() => handleOnClick(index + 1)}>
                <LeftChevronIcon />
              </Button>
            </MobileCrumbStyled>
          );
        }

        return (
          !isMobile && (
            <li key={index}>
              <LinkStyled
                modifier="inline"
                onClick={() => handleOnClick(index)}
                isActive={isActive || isMatchPathname}
              >
                <NumberStyled>{index + 1}</NumberStyled>
                {label}
              </LinkStyled>
            </li>
          )
        );
      })}
    </BreadcrumbsWrapperStyled>
  );
};

export default Breadcrumbs;
